<template>
  <div class="waterfall-container" ref="containerRef">
    <!-- 列容器 -->
    <div
      v-for="(col, colIndex) in columns"
      :key="colIndex"
      class="waterfall-column"
      :style="{width: `${100 / columnCount}%`,
        paddingLeft: !!(colIndex % 2) ? `${px2rem(gap / 2)}rem` : 0,
        paddingRight: !!(colIndex % 2) ? 0 : `${px2rem(gap / 2)}rem`,
      }">
      <!-- 每个列的项 -->
      <div v-for="item in col" :key="item.id || item._id" class="waterfall-item" :style="{ marginBottom: `${px2rem(gap)}rem` }">
        <slot :item="item"></slot>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';

const props = defineProps({
  // 数据列表
  list: {
    type: Array,
    default: () => [],
  },
  // 列数
  columns: {
    type: Number,
    default: 2,
  },
  // 间距（px）
  gap: {
    type: Number,
    default: 12,
  },
  // 响应式列数配置
  responsiveColumns: {
    type: Object,
    default: () => ({
      default: 2, // 默认3列
    }),
  },
});

// 响应式列数
const columnCount = ref(props.columns);
const columns = ref([]);
const containerRef = ref(null);

// 根据屏幕宽度计算列数
const calculateResponsiveColumns = () => {
  const width = window.innerWidth;
  const breakpoints = Object.keys(props.responsiveColumns)
    .filter((key) => key !== 'default')
    .map(Number)
    .sort((a, b) => a - b);
  for (let i = breakpoints.length - 1; i >= 0; i--) {
    if (width <= breakpoints[i]) {
      columnCount.value = props.responsiveColumns[breakpoints[i]];
      return;
    }
  }

  columnCount.value = props.responsiveColumns.default || props.columns;
};

// 瀑布流布局 - 分配项目到各列
const layoutWaterfall = () => {
  if (!props.list?.length) {
    columns.value = Array.from({ length: columnCount.value }, () => []);
    return;
  }

  // 重置列
  const tempColumns = Array.from({ length: columnCount.value }, () => []);

  // 简单平均分配（如果需要有高度计算的复杂分配，可以在此基础上扩展）
  props.list.forEach((item, index) => {
    const columnIndex = index % columnCount.value;
    tempColumns[columnIndex].push(item);
  });

  columns.value = tempColumns;
};

// 监听数据变化
watch(() => props.list, () => {
  nextTick(() => layoutWaterfall());
}, { deep: true });

// 监听列数变化
watch(columnCount, () => {
  layoutWaterfall();
});

/* utils */
const px2rem = (px) => {
  const fs = parseFloat(getComputedStyle(document.documentElement).fontSize);
  return px / fs;
};

// 监听窗口大小变化
const handleResize = () => {
  calculateResponsiveColumns();
};

onMounted(() => {
  calculateResponsiveColumns();
  layoutWaterfall();
  window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
});
</script>

<style scoped>
.waterfall-container {
  display: flex;
  width: 100%;
  box-sizing: border-box;
}

.waterfall-column {
  box-sizing: border-box;
}

.waterfall-item {
  break-inside: avoid;
  box-sizing: border-box;
}
</style>
